// Admin portal-settings editor — region vocabulary only. Branding (colors,
// landing/intake copy, value props) lives on the dedicated Branding page and
// the pricing margin on the Pricing page; this page owns the region's
// identity nouns the UI adapts to (display name, subdivision vocabulary,
// population source label).
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress, Button, Paper, TextField, Stack, Divider,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  fetchAdminSettings, saveAdminSettings, clearSaveError,
} from '../../store/admin-settings-slice.js';
import { fetchPublicConfig } from '../../store/settings-slice.js';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard.js';

// Builds the normalized save payload (and the value we diff against) so
// trimmed whitespace never looks dirty. Only the region keys this page owns.
const buildPayload = (form) => ({
  region_display_name: (form.region_display_name || '').trim(),
  population_source_label: (form.population_source_label || '').trim(),
  tile_billing_enabled: form.tile_billing_enabled === true,
  subdivision_primary: {
    singular: (form.subdivision_primary?.singular || '').trim(),
    plural: (form.subdivision_primary?.plural || '').trim(),
  },
  subdivision_secondary: {
    singular: (form.subdivision_secondary?.singular || '').trim(),
    plural: (form.subdivision_secondary?.plural || '').trim(),
  },
});

const AdminSettingsPage = () => {
  const dispatch = useDispatch();
  const {
    settings, loading, error, saving, saveError,
  } = useSelector((s) => s.adminSettings);
  const [form, setForm] = useState(null);

  useEffect(() => { dispatch(fetchAdminSettings()); }, [dispatch]);

  // Seed the form once settings arrive (and after a save resyncs them).
  useEffect(() => {
    if (settings) setForm(structuredClone(settings));
  }, [settings]);

  const dirty = useMemo(() => {
    if (!settings || !form) return false;
    return JSON.stringify(buildPayload(form)) !== JSON.stringify(buildPayload(settings));
  }, [form, settings]);

  useUnsavedChangesGuard(dirty);

  if (loading && !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading settings" />
      </Box>
    );
  }

  if (error && !form) return <Alert severity="error">{error}</Alert>;
  if (!form) return null;

  const setField = (key, value) => {
    if (saveError) dispatch(clearSaveError());
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setSubdivision = (key, part, value) => {
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], [part]: value } }));
  };

  const handleSave = async () => {
    const result = await dispatch(saveAdminSettings(buildPayload(form)));
    // Refresh the public config so the public pages pick up the new nouns.
    if (saveAdminSettings.fulfilled.match(result)) dispatch(fetchPublicConfig());
  };

  const handleReset = () => setForm(structuredClone(settings));

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap',
      }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Portal Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Region vocabulary for the public portal. Colors and page content
            live on the Branding page.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {dirty && <Typography variant="body2" color="text.secondary">Unsaved changes</Typography>}
          <Button onClick={handleReset} disabled={!dirty || saving}>Reset</Button>
          <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save changes'}
          </Button>
        </Stack>
      </Box>

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
          Region
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Region display name"
            value={form.region_display_name ?? ''}
            onChange={(e) => setField('region_display_name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Population source label"
            value={form.population_source_label ?? ''}
            onChange={(e) => setField('population_source_label', e.target.value)}
            fullWidth
          />
          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Subdivision vocabulary</Typography>
          </Divider>
          {[
            { key: 'subdivision_primary', label: 'Primary (e.g. county)' },
            { key: 'subdivision_secondary', label: 'Secondary (e.g. city)' },
          ].map((sub) => (
            <Box key={sub.key} sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={`${sub.label} — singular`}
                value={form[sub.key]?.singular ?? ''}
                onChange={(e) => setSubdivision(sub.key, 'singular', e.target.value)}
                fullWidth
              />
              <TextField
                label={`${sub.label} — plural`}
                value={form[sub.key]?.plural ?? ''}
                onChange={(e) => setSubdivision(sub.key, 'plural', e.target.value)}
                fullWidth
              />
            </Box>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Billing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls how the billable area is measured for every selection.
        </Typography>
        <FormControlLabel
          control={(
            <Switch
              checked={form.tile_billing_enabled === true}
              onChange={(e) => setField('tile_billing_enabled', e.target.checked)}
            />
          )}
          label="Bill by whole topo tiles"
        />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          When on, area, population, and pricing snap up to the whole USGS topo
          tiles a selection touches, and the tile grid is drawn on the map. When
          off (recommended), estimates use the true selected boundary and no
          grid is shown.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AdminSettingsPage;
